import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import {
  Trash2,
  Shield,
  UserPlus,
  X,
  Save,
  LayoutDashboard,
  Users,
  FileText,
  Megaphone,
  MessageCircle,
  BarChart2,
  Mail,
  Key,
  Building2,
  Sliders
} from 'lucide-react';

/** Roles workspace managers may assign (not owner/admin) */
const MEMBER_ASSIGNABLE_ROLES = ['member', 'agent', 'viewer'];

/** One toggle per sidebar area — maps to all granular permissions in that DB category */
const PERMISSION_MENU_ORDER = [
  'dashboard',
  'contacts',
  'templates',
  'campaigns',
  'chats',
  'statistics',
  'email',
  'api',
  'workspace',
  'settings'
];

const PERMISSION_MENU_LABELS = {
  dashboard: 'Dashboard',
  contacts: 'Contacts',
  templates: 'WhatsApp Templates',
  campaigns: 'Campaigns',
  chats: 'Chats',
  statistics: 'Statistics',
  email: 'Email',
  api: 'API & integrations',
  workspace: 'Workspace',
  settings: 'Settings'
};

const PERMISSION_MENU_HINTS = {
  dashboard: 'Overview and analytics',
  contacts: 'Contacts, import, and export',
  templates: 'WhatsApp template management',
  campaigns: 'WhatsApp campaigns',
  chats: 'Inbox and sending messages',
  statistics: 'WhatsApp and email stats',
  email: 'Email templates and campaigns',
  api: 'API tokens and public API settings',
  workspace: 'Workspace settings and members',
  settings: 'App and integration settings'
};

const PERMISSION_MENU_ICONS = {
  dashboard: LayoutDashboard,
  contacts: Users,
  templates: FileText,
  campaigns: Megaphone,
  chats: MessageCircle,
  statistics: BarChart2,
  email: Mail,
  api: Key,
  workspace: Building2,
  settings: Sliders
};

const WorkspaceUsers = () => {
  const { currentWorkspace } = useWorkspace();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});

  // Fetch workspace users
  const { data: usersData, isLoading } = useQuery(
    ['workspaceUsers', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return null;
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get(
        `/api/workspaces/${currentWorkspace.id}/users`,
        { headers }
      );
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Error fetching users:', error);
        toast.error('Failed to load workspace users');
      }
    }
  );

  // Fetch current user's permissions to check if they can manage users
  const { data: myPermissions, isLoading: permissionsLoading, error: permissionsError } = useQuery(
    ['myPermissions', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return null;
      const headers = { 'x-workspace-id': currentWorkspace.id };
      console.log('Fetching permissions for workspace:', currentWorkspace.id);
      try {
        const response = await apiClient.get(
          `/api/workspaces/${currentWorkspace.id}/users/me/permissions`,
          { headers }
        );
        console.log('Permissions response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching permissions:', error.response?.data || error.message);
        throw error;
      }
    },
    {
      enabled: !!currentWorkspace?.id,
      retry: 1
    }
  );

  // Owner and admin always have manage_users permission
  const canManage = myPermissions?.role === 'owner' ||
    myPermissions?.role === 'admin' ||
    myPermissions?.permissions?.includes('workspace.manage_users');

  // Add user mutation
  const addUserMutation = useMutation(
    async (userData) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.post(
        `/api/workspaces/${currentWorkspace.id}/users`,
        userData,
        { headers }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workspaceUsers', currentWorkspace?.id]);
        setShowAddModal(false);
        toast.success('User added successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to add user');
      }
    }
  );

  // Update role mutation
  const updateRoleMutation = useMutation(
    async ({ userId, role }) => {
      // Prevent changing own role on frontend
      if (currentUser?.id === userId) {
        throw new Error('You cannot change your own role');
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.patch(
        `/api/workspaces/${currentWorkspace.id}/users/${userId}/role`,
        { role },
        { headers }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workspaceUsers', currentWorkspace?.id]);
        // Invalidate permissions cache when role changes
        queryClient.invalidateQueries(['userPermissions']);
        toast.success('User role updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || error.message || 'Failed to update role');
      }
    }
  );

  // Update permissions mutation
  const updatePermissionsMutation = useMutation(
    async ({ userId, permissions }) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.patch(
        `/api/workspaces/${currentWorkspace.id}/users/${userId}/permissions`,
        { permissions },
        { headers }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workspaceUsers', currentWorkspace?.id]);
        // Invalidate permissions cache so menu updates immediately
        queryClient.invalidateQueries(['userPermissions']);
        setShowPermissionsModal(false);
        toast.success('Permissions updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update permissions');
      }
    }
  );

  // Remove user mutation
  const removeUserMutation = useMutation(
    async (userId) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.delete(
        `/api/workspaces/${currentWorkspace.id}/users/${userId}`,
        { headers }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workspaceUsers', currentWorkspace?.id]);
        toast.success('User removed successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to remove user');
      }
    }
  );

  // Fetch user permissions
  const fetchUserPermissions = async (userId) => {
    try {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get(
        `/api/workspaces/${currentWorkspace.id}/users/${userId}/permissions`,
        { headers }
      );
      return response.data;
    } catch (error) {
      toast.error('Failed to load user permissions');
      return null;
    }
  };

  const handleOpenPermissions = async (user) => {
    setSelectedUser(user);
    const data = await fetchUserPermissions(user.id);
    if (data) {
      setUserPermissions(data.permissions);
      setShowPermissionsModal(true);
    }
  };

  /** Toggle entire menu (category): grant or deny all underlying permissions */
  const handleMenuToggle = (category, enabled) => {
    setUserPermissions((prev) => {
      if (!prev[category]) return prev;
      return {
        ...prev,
        [category]: prev[category].map((p) => ({
          ...p,
          override: true,
          overrideValue: enabled,
          // Keep in sync so checkboxes / indeterminate recompute (API only sent granted before)
          granted: enabled
        }))
      };
    });
  };

  const handleSavePermissions = () => {
    const permissions = [];
    Object.values(userPermissions).forEach((categoryPermissions) => {
      categoryPermissions.forEach((perm) => {
        if (perm.override === true) {
          permissions.push({
            permissionId: perm.id,
            granted: perm.overrideValue === true
          });
        }
      });
    });

    updatePermissionsMutation.mutate({
      userId: selectedUser.id,
      permissions
    });
  };

  const getRoleColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      member: 'bg-green-100 text-green-800',
      agent: 'bg-yellow-100 text-yellow-800',
      viewer: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || colors.member;
  };

  // Show loading state while checking permissions
  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Loading permissions...</div>
      </div>
    );
  }

  // Show error if permissions failed to load
  if (permissionsError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">Error loading permissions</p>
          <p className="text-sm text-red-700 mt-2">
            {permissionsError.response?.data?.error || permissionsError.message}
          </p>
          <p className="text-xs text-red-600 mt-2">
            Workspace ID: {currentWorkspace?.id}
          </p>
        </div>
      </div>
    );
  }

  // Check permissions - owner and admin always have access
  if (!canManage && !permissionsLoading && myPermissions) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            You don't have permission to manage workspace users.
          </p>
          <p className="text-sm text-yellow-700 mt-2">
            Your role: {myPermissions?.role || 'Unknown'}
          </p>
          <p className="text-xs text-yellow-600 mt-1">
            Permissions: {myPermissions?.permissions?.length || 0} found
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Workspace Users</h1>
          <p className="text-gray-600 mt-1">Manage users and their permissions</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="h-5 w-5" />
          Add User
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : usersData?.users?.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                usersData?.users?.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.workspace_role === 'owner' ? (
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getRoleColor('owner')}`}
                          title="Owner is not changed from this list"
                        >
                          Owner
                        </span>
                      ) : (
                        <select
                          value={MEMBER_ASSIGNABLE_ROLES.includes(user.workspace_role) ? user.workspace_role : ''}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (!next) return;
                            updateRoleMutation.mutate({
                              userId: user.id,
                              role: next
                            });
                          }}
                          disabled={currentUser?.id === user.id}
                          className={`px-3 py-1 text-xs font-semibold rounded-full border-0 ${getRoleColor(user.workspace_role)} focus:outline-none focus:ring-2 focus:ring-blue-500 ${currentUser?.id === user.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                          title={currentUser?.id === user.id ? 'You cannot change your own role' : ''}
                        >
                          {user.workspace_role === 'admin' && (
                            <option value="">Change to…</option>
                          )}
                          <option value="member">Member</option>
                          <option value="agent">Agent</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.joined_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenPermissions(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Manage Permissions"
                        >
                          <Shield className="h-5 w-5" />
                        </button>
                        {user.workspace_role !== 'owner' && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to remove ${user.name}?`)) {
                                removeUserMutation.mutate(user.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Remove User"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onAdd={(userData) => addUserMutation.mutate(userData)}
          isLoading={addUserMutation.isLoading}
        />
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <PermissionsModal
          user={selectedUser}
          permissions={userPermissions}
          onMenuToggle={handleMenuToggle}
          onSave={handleSavePermissions}
          onClose={() => setShowPermissionsModal(false)}
          isLoading={updatePermissionsMutation.isLoading}
        />
      )}
    </div>
  );
};

// Add User Modal Component
const AddUserModal = ({ onClose, onAdd, isLoading }) => {
  const { currentWorkspace } = useWorkspace();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'member',
    isNewUser: false
  });
  const [emailExists, setEmailExists] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Check if email exists when email changes
  useEffect(() => {
    const checkEmail = async () => {
      if (!formData.email || !currentWorkspace?.id) {
        setEmailExists(null);
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setEmailExists(null);
        return;
      }

      setCheckingEmail(true);
      try {
        const headers = { 'x-workspace-id': currentWorkspace.id };
        const response = await apiClient.get(
          `/api/workspaces/${currentWorkspace.id}/users/check-email/${encodeURIComponent(formData.email)}`,
          { headers }
        );

        const exists = response.data.exists;
        setEmailExists(exists);

        // Auto-uncheck "Create new user account" if email exists
        if (exists && formData.isNewUser) {
          setFormData(prev => ({ ...prev, isNewUser: false }));
          toast.info('User exists. Inviting existing user.');
        }

        // Auto-check "Create new user account" if email doesn't exist
        if (!exists && !formData.isNewUser) {
          setFormData(prev => ({ ...prev, isNewUser: true }));
        }
      } catch (error) {
        console.error('Error checking email:', error);
        setEmailExists(null);
      } finally {
        setCheckingEmail(false);
      }
    };

    // Debounce email check
    const timeoutId = setTimeout(checkEmail, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.email, currentWorkspace?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate based on email existence
    if (emailExists && formData.isNewUser) {
      toast.error('User exists. Uncheck to invite.');
      return;
    }

    if (!emailExists && !formData.isNewUser) {
      toast.error('User not found. Check to create.');
      return;
    }

    if (formData.isNewUser && (!formData.name || !formData.password)) {
      toast.error('Name and password required');
      return;
    }

    if (formData.isNewUser && formData.password.length < 6) {
      toast.error('Password must be 6+ characters');
      return;
    }

    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add User to Workspace</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${emailExists === true
                    ? 'border-yellow-400 bg-yellow-50'
                    : emailExists === false
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300'
                  }`}
                placeholder="user@example.com"
              />
              {checkingEmail && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
            {emailExists === true && (
              <p className="mt-1 text-sm text-yellow-600">
                ✓ User exists. Will invite.
              </p>
            )}
            {emailExists === false && formData.email && (
              <p className="mt-1 text-sm text-green-600">
                ✓ Email available. Creating account.
              </p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isNewUser"
              checked={formData.isNewUser}
              onChange={(e) => {
                // Prevent checking if email exists
                if (e.target.checked && emailExists === true) {
                  toast.error('User exists. Cannot create.');
                  return;
                }
                // Prevent unchecking if email doesn't exist
                if (!e.target.checked && emailExists === false) {
                  toast.error('User not found. Must create.');
                  return;
                }
                setFormData({ ...formData, isNewUser: e.target.checked });
              }}
              disabled={emailExists === true}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label htmlFor="isNewUser" className={`ml-2 text-sm ${emailExists === true ? 'text-gray-500' : 'text-gray-700'}`}>
              Create new user account
              {emailExists === true && <span className="ml-1 text-xs text-yellow-600">(disabled)</span>}
            </label>
          </div>

          {formData.isNewUser && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  required={formData.isNewUser}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Full Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  required={formData.isNewUser}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Password (min 6 characters)"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="member">Member</option>
              <option value="agent">Agent</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? 'Adding...' : 'Add User'}
              {!isLoading && <UserPlus className="h-4 w-4" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/** Checkbox row: keeps indeterminate in sync after React commits (controlled + indeterminate quirk) */
const MenuAccessRow = ({ category, perms, label, hint, Icon, onMenuToggle }) => {
  const inputRef = useRef(null);
  const inputId = `menu-access-${category}`;
  const allGranted = perms.every((p) => p.granted);
  const someGranted = perms.some((p) => p.granted);
  const indeterminate = someGranted && !allGranted;

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (el) el.indeterminate = indeterminate;
  }, [indeterminate, perms]);

  return (
    <label
      htmlFor={inputId}
      className="flex cursor-pointer items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-blue-200 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-500"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 pointer-events-none">
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1 pointer-events-none">
        <div className="font-medium text-gray-900">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5">{hint}</div>
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        className="h-5 w-5 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        checked={allGranted}
        onChange={(e) => onMenuToggle(category, e.target.checked)}
        title={indeterminate ? 'Partial access — click to enable all' : undefined}
        aria-label={`${label} access`}
      />
    </label>
  );
};

// Permissions Modal — one toggle per app menu (all actions for that area on/off)
const PermissionsModal = ({ user, permissions, onMenuToggle, onSave, onClose, isLoading }) => {
  const categories = useMemo(() => {
    const keys = Object.keys(permissions || {});
    const ordered = PERMISSION_MENU_ORDER.filter((c) => keys.includes(c));
    const rest = keys.filter((c) => !PERMISSION_MENU_ORDER.includes(c)).sort();
    return [...ordered, ...rest];
  }, [permissions]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/80">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Menu access</h2>
            <p className="text-sm text-gray-600 mt-1">{user.name} ({user.email})</p>
            <p className="text-xs text-gray-500 mt-2">
              Turn each menu on or off. This sets access for that whole area (not separate view/edit).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded-lg p-1"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {categories.map((category) => {
            const perms = permissions[category];
            if (!perms?.length) return null;
            const Icon = PERMISSION_MENU_ICONS[category] || LayoutDashboard;
            const label = PERMISSION_MENU_LABELS[category] || category.replace(/_/g, ' ');
            const hint = PERMISSION_MENU_HINTS[category] || `Access to ${label}`;

            return (
              <MenuAccessRow
                key={category}
                category={category}
                perms={perms}
                label={label}
                hint={hint}
                Icon={Icon}
                onMenuToggle={onMenuToggle}
              />
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? 'Saving...' : 'Save'}
            {!isLoading && <Save className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceUsers;

